import { FormEvent, useEffect, useState } from 'react';
import { Button, Form, Input, Modal } from 'semantic-ui-react';
import { useReplaceBlockTagMutation } from 'api/tagsApi';
import { ErrorMessage } from 'components/error-message';

type Props = {
  tagEntry: {
    tag: string;
    count: number;
  } | null;
  onClose(): void;
};

export function BlockTagReplaceModal({ tagEntry, onClose }: Props) {
  const { error, isLoading, mutateAsync, reset } = useReplaceBlockTagMutation();
  const [replacementTag, setReplacementTag] = useState('');
  const [validationError, setValidationError] = useState<Error | null>(null);

  useEffect(() => {
    setReplacementTag('');
    setValidationError(null);
    reset();
  }, [reset, tagEntry?.tag]);

  if (!tagEntry) {
    return null;
  }

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    setReplacementTag('');
    setValidationError(null);
    reset();
    onClose();
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    const nextTag = replacementTag.trim();

    if (!nextTag) {
      setValidationError(new Error('Please enter a replacement tag.'));
      return;
    }

    if (nextTag === tagEntry.tag) {
      setValidationError(new Error('Replacement tag must be different from the current tag.'));
      return;
    }

    setValidationError(null);

    try {
      await mutateAsync({ tag: tagEntry.tag, replacementTag: nextTag });
      setReplacementTag('');
      setValidationError(null);
      reset();
      onClose();
    } catch {
      // The mutation state already exposes the error in the dialog.
    }
  };

  return (
    <Modal as={Form} open size="tiny" onClose={handleClose} onSubmit={handleSubmit} closeOnDimmerClick={!isLoading} closeOnEscape={!isLoading}>
      <Modal.Header>Replace tag</Modal.Header>
      <Modal.Content>
        <p>
          Replace the tag <strong>{tagEntry.tag}</strong> on {tagEntry.count} {tagEntry.count === 1 ? 'block' : 'blocks'} with:
        </p>
        <Form.Field>
          {/* <label>Replacement tag</label> */}
          <Input
            label="Replacement tag"
            required
            value={replacementTag}
            placeholder="Enter new tag"
            onChange={(_, { value }) => {
              setReplacementTag(value);

              if (validationError) {
                setValidationError(null);
              }
            }}
          />
        </Form.Field>
        <ErrorMessage error={validationError ?? error ?? null} />
      </Modal.Content>
      <Modal.Actions>
        <Button color="black" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button type="submit" primary loading={isLoading} disabled={isLoading}>
          Replace
        </Button>
      </Modal.Actions>
    </Modal>
  );
}
